import { Button, Form, Input, Radio, Select, Switch, Tag } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { cardApi } from '../../../../../apis'
import {
  ActionEnum,
  AlertMessageEnum,
  getDateFormat,
} from '../../../../../common'
import {
  LoadingDataComponent,
  ManageModalComponent,
  ManageModalFooterComponent,
  NumberFormat,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import {
  CardEntity,
  CardTypeEnum,
  CreateCardDto,
  UserTypeEnum,
} from '../../../../../models'
import { RootState } from '../../../../../stores'
import {
  loadCards,
  loadProfile,
  loadUsers,
  setAction,
  setActionProcessing,
  setAlert,
  setShowManageModal,
  setShowPayInModal,
} from '../../../../../stores/actions'

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

const StyledLabel = styled.span`
  font-weight: bold;
`

export const ManageCardModalComponent: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { management, regex } = useSelector((state: RootState) => state.common)
  const { selectedCardId } = useSelector((state: RootState) => state.card)
  const { users, isUsersLoaded } = useSelector((state: RootState) => state.user)
  const [form] = Form.useForm()

  const [card, setCard] = React.useState<CardEntity>()
  const [loading, setLoading] = React.useState(false)
  const [isCustomerCard, setIsCustomerCard] = React.useState(false)

  React.useEffect(() => {
    if (selectedCardId) {
      const loadData = async () => {
        try {
          setLoading(true)
          const resp = await cardApi.getCardById(selectedCardId)
          setCard(resp.data)
          if (!resp.data.customer) {
            dispatch(
              setAlert({
                key: 'card.empty.notification',
                type: AlertMessageEnum.INFO,
              })
            )
          } else {
            setIsCustomerCard(true)
          }
        } catch (e) {
          if (e.response && e.response.status && e.response.status === 401) {
            setLoading(true)
            const resp = await cardApi.getCardById(selectedCardId)
            setCard(resp.data)
            if (!resp.data.customer) {
              dispatch(
                setAlert({
                  key: 'card.empty.notification',
                  type: AlertMessageEnum.INFO,
                })
              )
            } else {
              setIsCustomerCard(true)
            }
          } else {
            dispatch(
              setAlert({
                key: 'card.loadData.fail',
                type: AlertMessageEnum.ERROR,
              })
            )
          }
        } finally {
          setLoading(false)
        }
      }
      loadData()
    }
    if (!isUsersLoaded) {
      dispatch(loadUsers())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (
      management.action === ActionEnum.DELETE &&
      card &&
      card.accountsPayable > 0
    ) {
      dispatch(
        setAlert({
          key: 'card.warning.accountsPayable',
          type: AlertMessageEnum.WARNING,
        })
      )
    }
  }, [card, dispatch, management.action])

  const canDelete = React.useMemo(() => {
    if (management.action === ActionEnum.DELETE) {
      return card && card.accountsPayable <= 0
    }
    return (
      management.action === ActionEnum.CREATE ||
      (!!card && management.alertMessage?.type !== AlertMessageEnum.SUCCESS)
    )
  }, [card, management.action, management.alertMessage?.type])

  const onFinish = async (value: CreateCardDto) => {
    dispatch(setAlert())
    dispatch(setActionProcessing(true))
    switch (management.action) {
      case ActionEnum.VIEW:
        dispatch(setAction(ActionEnum.UPDATE))
        break
      case ActionEnum.CREATE:
        try {
          await cardApi.createCard(isCustomerCard ? value : undefined)
          dispatch(loadCards())
          dispatch(
            setAlert({
              key: 'card.create.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
        } catch (e) {
          if (e.response && e.response.status) {
            if (e.response.status === 401) {
              dispatch(loadProfile())
              await cardApi.createCard(isCustomerCard ? value : undefined)
              dispatch(loadCards())
              dispatch(
                setAlert({
                  key: 'card.create.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else if (e.response.status === 409) {
              dispatch(
                setAlert({
                  key: `card.duplicate.${e.response.data.error.field}`,
                  type: AlertMessageEnum.ERROR,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key: 'card.create.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
      case ActionEnum.UPDATE:
        if (selectedCardId) {
          try {
            await cardApi.updateCard(selectedCardId, { ...value })
            dispatch(loadCards())
            setCard(undefined)
            dispatch(
              setAlert({
                key: 'card.update.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e.response && e.response.status) {
              if (e.response.status === 401) {
                dispatch(loadProfile())
                await cardApi.updateCard(selectedCardId, { ...value })
                dispatch(loadCards())
                setCard(undefined)
                dispatch(
                  setAlert({
                    key: 'card.update.success',
                    type: AlertMessageEnum.SUCCESS,
                  })
                )
              } else if (e.response.status === 409) {
                dispatch(
                  setAlert({
                    key: `card.duplicate.${e.response.data.error.field}`,
                    type: AlertMessageEnum.ERROR,
                  })
                )
              }
            } else {
              dispatch(
                setAlert({
                  key: 'card.update.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
      case ActionEnum.DELETE:
        if (selectedCardId) {
          try {
            await cardApi.deleteCard(selectedCardId)
            dispatch(loadCards())
            setCard(undefined)
            dispatch(
              setAlert({
                key: 'card.delete.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e.response && e.response.status && e.response.status === 401) {
              dispatch(loadProfile())
              await cardApi.deleteCard(selectedCardId)
              dispatch(loadCards())
              setCard(undefined)
              dispatch(
                setAlert({
                  key: 'card.delete.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key:
                    e.response && e.response.status && e.response.status === 400
                      ? 'common.delete.fail.fkConstraint'
                      : 'card.delete.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
      default:
        break
    }
    dispatch(setActionProcessing(false))
  }

  const body = (
    <Form onFinish={onFinish} form={form}>
      {loading && <LoadingDataComponent />}
      {!loading && card && management.action !== ActionEnum.CREATE && (
        <>
          <Form.Item
            label={<StyledLabel>{translate('card.id', language)}</StyledLabel>}
          >
            {card.id}
          </Form.Item>
          <Form.Item
            label={
              <StyledLabel>{translate('card.type', language)}</StyledLabel>
            }
          >
            <Tag color={card.customer ? 'success' : 'default'}>
              {translate(
                `card.type.${card.customer ? 'customer' : 'empty'}`,
                language
              )}
            </Tag>
          </Form.Item>
          {card.customer && (
            <>
              <Form.Item
                label={
                  <StyledLabel>
                    {translate('common.customer', language)}
                  </StyledLabel>
                }
              >
                {card.customer.name}
              </Form.Item>
              <Form.Item
                label={
                  <StyledLabel>
                    {translate('card.availableBalance', language)}
                  </StyledLabel>
                }
              >
                <NumberFormat
                  value={card.availableBalance}
                  suffixKey="common.moneyType"
                />
              </Form.Item>
            </>
          )}
        </>
      )}
      {!loading && (
        <>
          {card &&
            [ActionEnum.VIEW, ActionEnum.DELETE].includes(
              management.action
            ) && (
              <>
                {card.customer && (
                  <>
                    <Form.Item
                      label={
                        <StyledLabel>
                          {translate('common.createdDate', language)}
                        </StyledLabel>
                      }
                    >
                      {getDateFormat(card.createdDate)}
                    </Form.Item>
                    <Form.Item
                      label={
                        <StyledLabel>
                          {translate('card.driverName', language)}
                        </StyledLabel>
                      }
                    >
                      {card.driverName}
                    </Form.Item>
                    <Form.Item
                      label={
                        <StyledLabel>
                          {translate('card.driverPhone', language)}
                        </StyledLabel>
                      }
                    >
                      {card.driverPhone}
                    </Form.Item>
                    <Form.Item
                      label={
                        <StyledLabel>
                          {translate('card.licensePlate', language)}
                        </StyledLabel>
                      }
                    >
                      {card.licensePlate}
                    </Form.Item>
                    <Form.Item
                      label={
                        <StyledLabel>
                          {translate('common.accountsPayable', language)}
                        </StyledLabel>
                      }
                    >
                      <NumberFormat
                        value={card.accountsPayable}
                        suffixKey="common.moneyType"
                      />
                    </Form.Item>
                    <Form.Item
                      label={
                        <StyledLabel>
                          {translate('card.debtLimit', language)}
                        </StyledLabel>
                      }
                    >
                      <NumberFormat
                        value={card.debtLimit}
                        suffixKey="common.moneyType"
                      />
                    </Form.Item>
                    <Form.Item
                      label={
                        <StyledLabel>
                          {translate('card.initialDebt', language)}
                        </StyledLabel>
                      }
                    >
                      <NumberFormat
                        value={card.initialDebt}
                        suffixKey="common.moneyType"
                      />
                    </Form.Item>
                    <Form.Item
                      label={
                        <StyledLabel>
                          {translate('card.limitSetDate', language)}
                        </StyledLabel>
                      }
                    >
                      {getDateFormat(card.limitSetDate)}
                    </Form.Item>
                    <Form.Item
                      label={
                        <StyledLabel>
                          {translate('card.creator', language)}
                        </StyledLabel>
                      }
                    >
                      {card.creator.name}
                    </Form.Item>
                    <Form.Item
                      label={
                        <StyledLabel>
                          {translate('common.status', language)}
                        </StyledLabel>
                      }
                    >
                      <Tag color={card.active ? 'success' : 'default'}>
                        {translate(
                          `card.status.${
                            card.active ? 'activated' : 'deactivated'
                          }`,
                          language
                        ).toUpperCase()}
                      </Tag>
                    </Form.Item>
                  </>
                )}
              </>
            )}
          {management.alertMessage?.type !== AlertMessageEnum.SUCCESS &&
            [ActionEnum.CREATE, ActionEnum.UPDATE].includes(
              management.action
            ) && (
              <>
                {(management.action === ActionEnum.CREATE ||
                  (card && !card.customer)) && (
                  <>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('card.type', language)}
                        </StyledLabel>
                      }
                      name="cardType"
                      initialValue={
                        card
                          ? card.customer
                            ? CardTypeEnum.CUSTOMER
                            : CardTypeEnum.EMPTY
                          : undefined
                      }
                      rules={[
                        {
                          required: true,
                          message: translate('card.required.type', language),
                        },
                      ]}
                    >
                      <Radio.Group
                        onChange={(e) => {
                          setIsCustomerCard(
                            e.target.value === CardTypeEnum.CUSTOMER
                          )
                        }}
                      >
                        {Object.values(CardTypeEnum).map((item) => {
                          return (
                            <Radio value={item} key={item}>
                              {translate(
                                `card.type.${item.toLowerCase()}`,
                                language
                              )}
                            </Radio>
                          )
                        })}
                      </Radio.Group>
                    </StyledFormItem>
                    {isCustomerCard && (
                      <StyledFormItem
                        name="customerId"
                        label={
                          <StyledLabel>
                            {translate('common.customer', language)}
                          </StyledLabel>
                        }
                        rules={[
                          {
                            required: true,
                            message: translate(
                              'card.required.customer',
                              language
                            ),
                          },
                        ]}
                      >
                        <Select
                          placeholder={translate('common.customer', language)}
                          style={{ width: '100%' }}
                          loading={!isUsersLoaded}
                          disabled={!isUsersLoaded}
                        >
                          {users
                            .filter(
                              (item) =>
                                item.userType.type === UserTypeEnum.CUSTOMER
                            )
                            .map((item) => (
                              <Select.Option key={item.id} value={item.id}>
                                {`${item.name} ( ${item.phone} )`}
                              </Select.Option>
                            ))}
                        </Select>
                      </StyledFormItem>
                    )}
                  </>
                )}
                {isCustomerCard && (
                  <>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('card.driverName', language)}
                        </StyledLabel>
                      }
                      name="driverName"
                      initialValue={card?.driverName || undefined}
                      rules={[
                        {
                          required: true,
                          message: '',
                        },
                        () => ({
                          validator(_, value) {
                            if (!value || !value.trim().length) {
                              return Promise.reject(
                                new Error(
                                  translate('card.invalid.driverName', language)
                                )
                              )
                            }
                            return Promise.resolve()
                          },
                        }),
                      ]}
                    >
                      <Input
                        placeholder={translate('card.driverName', language)}
                        allowClear
                      />
                    </StyledFormItem>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('card.driverPhone', language)}
                        </StyledLabel>
                      }
                      name="driverPhone"
                      initialValue={card?.driverPhone}
                      rules={[
                        {
                          required: true,
                          message: translate(
                            'card.invalid.driverPhone',
                            language
                          ),
                          pattern: regex.phone,
                        },
                      ]}
                    >
                      <Input
                        placeholder={translate('card.driverPhone', language)}
                        allowClear
                      />
                    </StyledFormItem>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('card.licensePlate', language)}
                        </StyledLabel>
                      }
                      name="licensePlate"
                      initialValue={card?.licensePlate}
                      rules={[
                        {
                          required: true,
                          message: '',
                        },
                        () => ({
                          validator(_, value) {
                            if (!value || !value.trim().length) {
                              return Promise.reject(
                                new Error(
                                  translate(
                                    'card.invalid.licensePlate',
                                    language
                                  )
                                )
                              )
                            }
                            return Promise.resolve()
                          },
                        }),
                      ]}
                    >
                      <Input
                        placeholder={translate('card.licensePlate', language)}
                        allowClear
                      />
                    </StyledFormItem>
                    <StyledFormItem
                      name="debtLimit"
                      label={
                        <StyledLabel>
                          {translate('card.debtLimit', language)}
                        </StyledLabel>
                      }
                      initialValue={card?.debtLimit || 0}
                      rules={[
                        {
                          required: true,
                          message: '',
                        },
                        () => ({
                          validator(_, value) {
                            if (!value || value < 0) {
                              return Promise.reject(
                                new Error(
                                  translate('card.invalid.debtLimit', language)
                                )
                              )
                            }
                            return Promise.resolve()
                          },
                        }),
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={translate('card.debtLimit', language)}
                      />
                    </StyledFormItem>
                    <StyledFormItem
                      label={
                        <StyledLabel>
                          {translate('common.status', language)}
                        </StyledLabel>
                      }
                      name="active"
                      initialValue={card?.active}
                    >
                      <Switch
                        checkedChildren={translate(
                          'card.status.activated',
                          language
                        )}
                        unCheckedChildren={translate(
                          'card.status.deactivated',
                          language
                        )}
                        defaultChecked={card?.active}
                      />
                    </StyledFormItem>
                  </>
                )}
              </>
            )}
          <ManageModalFooterComponent
            hasDefaultButtons={canDelete}
            onDeleteConfirm={() => form.submit()}
            customButtons={
              <>
                {management.action === ActionEnum.UPDATE &&
                  card &&
                  card.customer && (
                    <Button
                      type="ghost"
                      style={{
                        outline: 'none',
                        marginRight: '10px',
                      }}
                      disabled={management.actionProcessing}
                      onClick={() => {
                        dispatch(setShowManageModal(false))
                        dispatch(setShowPayInModal(true))
                      }}
                    >
                      {translate('card.payIn', language)}
                    </Button>
                  )}
              </>
            }
          />
        </>
      )}
    </Form>
  )

  return <ManageModalComponent body={body} />
}
